import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ConfigOptions, ConfigService } from '@wilson/config';
import {
  Absence,
  Client,
  CorePayrollCategory,
  OvertimeDetail,
  PayrollCategoryName,
  Shift,
  Stay,
  SumOfCategories,
  SumOfCategoriesWithTemporarySum,
  PayrollUnitName,
} from '@wilson/interfaces';
import { TransformMasterDataTranslationService } from '@wilson/non-domain-specific/master-data-translate';
import { combineLatest, map, Observable } from 'rxjs';
import { PayrollService } from '../payroll/payroll.service';
export interface AggregatedOvertimeDetail {
  name: PayrollCategoryName | 'overtimeInMinutes';
  total: number;
  wageReportUnit: PayrollUnitName;
}

export interface AggregatedWorktimeDataAndPayrollCategoryValues {
  acceptedShiftCount: number;
  totalShiftCount: number;
  sumOfCategories: AggregatedOvertimeDetail[];
}

export interface AbsenceSummary {
  id: string;
  status: string;
  absenceCategoryId: string;
  absentFrom: string;
  absentTo: string;
}
export interface MonthlyWorktimeOverview {
  date: string;
  isPublicHoliday: boolean;
  shifts: Partial<Shift>[];
  absences: Absence[];
  stays: Partial<Stay>[];
  clients: Partial<Client>[];
}

export type UserWorkingTime = MonthlyWorktimeOverview & {
  overtimeDetails: OvertimeDetail | null;
};

@Injectable({
  providedIn: 'root',
})
export class UserWorktimeGatewayService {
  private readonly httpClient = inject(HttpClient);
  private readonly config = inject<ConfigOptions>(ConfigService);
  protected readonly payrollService = inject(PayrollService);
  protected readonly transformMasterDataTranslationService = inject(
    TransformMasterDataTranslationService,
  );

  protected readonly path = 'user-worktime';

  public getMonthlyWorktimeOverview(
    userId: string,
    month: string,
  ): Observable<MonthlyWorktimeOverview[]> {
    return this.httpClient.get<MonthlyWorktimeOverview[]>(
      `${this.config.host}/${this.path}/${userId}/monthly-worktime-overview?yearMonth=${month}`,
    );
  }

  public getPayrollCategoriesSummary<
    T extends OvertimeDetail<SumOfCategories | SumOfCategoriesWithTemporarySum>,
  >(userId: string, month: string, includeTemporary = false): Observable<T[]> {
    return combineLatest([
      this.payrollService.getPayrollCategories(),
      this.httpClient.get<T[]>(
        `${this.config.host}/${this.path}/${userId}/payroll-categories-summary?month=${month}&includeTemporary=${includeTemporary}`,
      ),
    ]).pipe(
      map(([payrollCategories, payrollCategoriesSummary]) =>
        payrollCategoriesSummary.map((summary) => {
          const updatedOvertimeDetail = summary.sumOfCategories.map(
            this.addTranslationsToOvertimeDetail(payrollCategories),
          );

          const sumOfCategories =
            this.transformMasterDataTranslationService.transform(
              updatedOvertimeDetail,
            );
          return { ...summary, sumOfCategories };
        }),
      ),
    );
  }

  private addTranslationsToOvertimeDetail(
    payrollCategories: CorePayrollCategory[],
  ): (sumOfCategory: SumOfCategories) => SumOfCategories {
    return (sumOfCategory: SumOfCategories) => {
      const matchingCategory = payrollCategories.find(
        (category) => category.name === sumOfCategory.name,
      );

      return matchingCategory
        ? {
            ...sumOfCategory,
            nameDe: matchingCategory?.nameDe || '',
            nameEn: matchingCategory?.nameEn || '',
            namePl: matchingCategory?.namePl || '',
            nameFr: matchingCategory?.nameFr || '',
          }
        : sumOfCategory;
    };
  }
}
